import { supabase } from "../lib/supabase";

// Types for audit data

export interface AuditSupplier {
  id: number;
  name: string;
}

export interface AuditStoreActivity {
  id: number;
  storeName: string;
  storeReference: string;
  storeId: number;
}

export interface AuditMarketingActivity {
  id: number;
  periodName: string;
  periodStart: string;
  periodEnd: string;
  productName: string;
  activityTypeName: string;
  storeActivities: AuditStoreActivity[];
}

export interface AuditSupplierWithActivities extends AuditSupplier {
  marketingActivities: AuditMarketingActivity[];
}

export interface AuditProof {
  target: "store" | "product";
  code: string;
  description: string;
  order: number;
  imageUrl: string;
  timestamp: string;
}

/**
 * Fetch suppliers assigned to the current auditor.
 */
export async function fetchAuditorSuppliers(): Promise<AuditSupplier[]> {
  const { data, error } = await supabase
    .from("auditor_suppliers")
    .select(
      `
      supplier_id,
      suppliers(id, name)
    `,
    )
    .order("supplier_id");

  if (error) throw error;

  return (data ?? [])
    .filter((d) => d.suppliers)
    .map((d) => ({
      id: (d.suppliers as { id: number; name: string }).id,
      name: (d.suppliers as { id: number; name: string }).name,
    }));
}

/**
 * Fetch approved store activities for a supplier, grouped by marketing activity.
 * Traverses: supplier → brand → product → marketing_activity → store_activity
 */
export async function fetchApprovedActivitiesForSupplier(
  supplierId: number,
): Promise<AuditMarketingActivity[]> {
  // Query approved store activities for products belonging to this supplier's brands
  const { data, error } = await supabase
    .from("store_activities")
    .select(
      `
      id,
      store_id,
      marketing_activity_id,
      stores!inner(id, name, reference_number),
      marketing_activities!inner(
        id,
        periods(id, name, start_date, end_date),
        products!inner(
          id, 
          name,
          brands!inner(id, supplier_id)
        ),
        marketing_activity_types(id, name)
      )
    `,
    )
    .eq("is_approved", true)
    .eq("is_deleted", false)
    .eq("marketing_activities.products.brands.supplier_id", supplierId);

  if (error) throw error;

  // Group by marketing activity
  const activitiesMap = new Map<number, AuditMarketingActivity>();

  for (const sa of data ?? []) {
    const ma = sa.marketing_activities as {
      id: number;
      periods: {
        id: number;
        name: string;
        start_date: string;
        end_date: string;
      } | null;
      products: {
        id: number;
        name: string;
        brands: { id: number; supplier_id: number };
      };
      marketing_activity_types: { id: number; name: string } | null;
    };

    if (!ma.periods) continue;

    const maId = ma.id;
    const store = sa.stores as {
      id: number;
      name: string;
      reference_number: string;
    };

    if (!activitiesMap.has(maId)) {
      activitiesMap.set(maId, {
        id: maId,
        periodName: ma.periods.name,
        periodStart: ma.periods.start_date,
        periodEnd: ma.periods.end_date,
        productName: ma.products.name,
        activityTypeName: ma.marketing_activity_types?.name ?? "",
        storeActivities: [],
      });
    }

    activitiesMap.get(maId)!.storeActivities.push({
      id: sa.id,
      storeName: store.name,
      storeReference: store.reference_number ?? "",
      storeId: store.id,
    });
  }

  // Sort by period start date (descending), then by product name
  return Array.from(activitiesMap.values()).sort((a, b) => {
    const dateCompare = b.periodStart.localeCompare(a.periodStart);
    if (dateCompare !== 0) return dateCompare;
    return a.productName.localeCompare(b.productName);
  });
}

/**
 * Fetch proofs for a store activity with signed URLs.
 * Returns store proof, angle_1, and angle_2 proofs.
 */
export async function fetchProofsForStoreActivity(
  storeActivityId: number,
  storeId: number,
  periodStart: string,
  periodEnd: string,
): Promise<AuditProof[]> {
  const proofs: AuditProof[] = [];

  // Fetch product proofs with product requirement info
  const { data: productProofs, error: ppError } = await supabase
    .from("product_proofs")
    .select(
      `
      id,
      product_requirement_id,
      product_requirements(id, code, description, "order"),
      proofs!inner(id, image_url, created_at, status)
    `,
    )
    .eq("store_activity_id", storeActivityId)
    .eq("proofs.status", "approved");

  if (ppError) throw ppError;

  for (const pp of productProofs ?? []) {
    const proof = pp.proofs as {
      id: number;
      image_url: string;
      created_at: string;
      status: string;
    };
    const req = pp.product_requirements as {
      id: number;
      code: string;
      description: string;
      order: number;
    } | null;
    const signedUrl = await getSignedUrl(proof.image_url);
    proofs.push({
      target: "product",
      code: req?.code ?? "unknown",
      description: req?.description ?? "",
      order: req?.order ?? 99,
      imageUrl: signedUrl,
      timestamp: proof.created_at,
    });
  }

  // Fetch store proof (within period date range)
  const { data: storeProofs, error: spError } = await supabase
    .from("store_proofs")
    .select(
      `
      id,
      proofs!inner(id, image_url, created_at, status)
    `,
    )
    .eq("store_id", storeId)
    .eq("proofs.status", "approved")
    .gte("proofs.created_at", periodStart)
    .lte("proofs.created_at", `${periodEnd}T23:59:59.999Z`);

  if (spError) throw spError;

  if (storeProofs && storeProofs.length > 0) {
    // Take the most recent store proof
    const sp = storeProofs[0];
    const proof = sp.proofs as {
      id: number;
      image_url: string;
      created_at: string;
      status: string;
    };
    const signedUrl = await getSignedUrl(proof.image_url);
    proofs.push({
      target: "store",
      code: "store",
      description: "",
      order: 0,
      imageUrl: signedUrl,
      timestamp: proof.created_at,
    });
  }

  // Sort: store first (order 0), then product proofs by order
  return proofs.sort((a, b) => a.order - b.order);
}

/**
 * Get a signed URL for a proof image.
 */
async function getSignedUrl(imagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from("proof-images")
    .createSignedUrl(imagePath, 3600); // 1 hour expiry

  if (error) {
    console.error("Error creating signed URL:", error);
    return imagePath; // Fallback to original path
  }

  return data.signedUrl;
}

/**
 * Fetch all data needed for ZIP download for a supplier.
 * Returns comprehensive data including image URLs and metadata.
 */
export interface ZipDownloadData {
  supplierName: string;
  activities: {
    periodName: string;
    productName: string;
    activityTypeName: string;
    storeActivities: {
      storeReference: string;
      proofs: AuditProof[];
    }[];
  }[];
}

export async function fetchZipDownloadData(
  supplierId: number,
): Promise<ZipDownloadData> {
  // Get supplier name
  const { data: supplier, error: supplierError } = await supabase
    .from("suppliers")
    .select("name")
    .eq("id", supplierId)
    .single();

  if (supplierError) throw supplierError;

  // Get all approved activities
  const activities = await fetchApprovedActivitiesForSupplier(supplierId);

  // Fetch proofs for each store activity
  const activitiesWithProofs = await Promise.all(
    activities.map(async (ma) => {
      const storeActivitiesWithProofs = await Promise.all(
        ma.storeActivities.map(async (sa) => {
          const proofs = await fetchProofsForStoreActivity(
            sa.id,
            sa.storeId,
            ma.periodStart,
            ma.periodEnd,
          );
          return {
            storeReference: sa.storeReference,
            proofs,
          };
        }),
      );

      return {
        periodName: ma.periodName,
        productName: ma.productName,
        activityTypeName: ma.activityTypeName,
        storeActivities: storeActivitiesWithProofs,
      };
    }),
  );

  return {
    supplierName: supplier.name,
    activities: activitiesWithProofs,
  };
}
